import { RequestFilterTypeEnum, RequestTypeEnum } from "@nlc/shared-types";
import { Button as RACButton } from "react-aria-components";

import { ToggleButton, ToggleButtonGroup } from "@/components/button";
import LockIcon from "@/features/request/pick-type/assets/lock.svg?react";
import newCapabilitiesModalImage from "@/features/request/pick-type/assets/new-capabilites-modal.png";
import { UpgradeModal } from "@/features/request/pick-type/components/UpgradeModal";

import type { RequestFilterType } from "../types/filters";

import { useRequestStatistics, useSubscriptionPermissions } from "../hooks";

interface RequestFiltersProps {
  onFilterChange: (filter: RequestFilterType) => void;
  selectedFilter: RequestFilterType;
}

type FilterRequestType =
  | RequestFilterTypeEnum.GRAPHIC
  | RequestFilterTypeEnum.VIDEO
  | RequestFilterTypeEnum.WEBUI;

const getUpgradeModalProps = (requestType: FilterRequestType) => {
  switch (requestType) {
    case RequestFilterTypeEnum.GRAPHIC: {
      return {
        bodyText:
          "Unlock the ability to request social media ads, Amazon graphics, print designs, branding and much more!",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
    case RequestFilterTypeEnum.VIDEO: {
      return {
        bodyText:
          "Unlock the ability to request social media video ads, Amazon videos, advanced animations and long form video editing!",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
    case RequestFilterTypeEnum.WEBUI: {
      return {
        bodyText:
          "Unlock the ability to request custom website designs, intuitive UI kits, and high-impact landing pages that drive results.",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
  }
};

export function RequestFilters({
  onFilterChange,
  selectedFilter,
}: RequestFiltersProps) {
  const { data: subscriptionPermissions } = useSubscriptionPermissions();
  const { data: statistics } = useRequestStatistics();

  // If permissions are not loaded yet, show all filters without locks
  const permissions = subscriptionPermissions?.permissions ?? {
    hasGraphic: true,
    hasVideo: true,
    hasWebsite: true,
  };

  // Check if user has existing requests of each type
  const hasExistingRequests = {
    graphic: (statistics?.byType?.[RequestTypeEnum.GRAPHIC] || 0) > 0,
    video: (statistics?.byType?.[RequestTypeEnum.VIDEO] || 0) > 0,
    website:
      (statistics?.byType?.[RequestTypeEnum.WEBSITE_DESIGN] || 0) +
        (statistics?.byType?.[RequestTypeEnum.WEBSITE_IMPLEMENTATION] || 0) >
      0,
  };

  const renderLockedButton = (
    requestType: FilterRequestType,
    label: string,
    className: string,
  ) => {
    const modalProps = getUpgradeModalProps(requestType);

    return (
      <UpgradeModal
        bodyText={modalProps.bodyText}
        headerText={modalProps.headerText}
        heroImage={modalProps.heroImage}
        onConfirm={() => {
          window.open("https://nlc.com/manage-my-account/", "_blank");
        }}
      >
        <RACButton
          className={`${className} relative inline-flex justify-center items-center whitespace-nowrap rounded-md outline-hidden font-semibold focus-visible:outline-4 focus-visible:outline-offset-0 text-sm px-lg py-md gap-xs text-utility-gray-500 hover:bg-button-secondary-border-hover/15 border border-border-primary`}
          type="button"
        >
          <LockIcon className="w-5 h-5 stroke-2 text-utility-gray-400" />
          {label}
        </RACButton>
      </UpgradeModal>
    );
  };

  const renderFilterableLockButton = (
    requestType: FilterRequestType,
    label: string,
    className: string,
  ) => {
    const filterValue: RequestFilterType = requestType;

    return (
      <ToggleButton
        className={`cursor-pointer ${className} ${
          selectedFilter === filterValue
            ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
            : "text-utility-gray-500 hover:bg-button-secondary-border-hover/15"
        }`}
        color="gray"
        id={filterValue}
        onPress={() => onFilterChange(filterValue)}
        size="sm"
        variant="secondary"
      >
        <LockIcon className="w-5 h-5 stroke-2 text-utility-gray-400 mr-xs" />
        {label}
      </ToggleButton>
    );
  };

  return (
    <ToggleButtonGroup
      className="ml-0 shadow-xs w-full lg:w-auto"
      onSelectionChange={(keys) => {
        const selected = [...keys][0] as RequestFilterType;

        if (selected) {
          onFilterChange(selected);
        }
      }}
      selectedKeys={[selectedFilter]}
      selectionMode="single"
    >
      <ToggleButton
        className={`cursor-pointer flex-1 lg:flex-none lg:w-16 ${
          selectedFilter === RequestFilterTypeEnum.ALL
            ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
            : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
        }`}
        color="gray"
        id={RequestFilterTypeEnum.ALL}
        size="sm"
        variant="secondary"
      >
        All
      </ToggleButton>
      {permissions.hasGraphic ? (
        <ToggleButton
          className={`cursor-pointer flex-1 lg:flex-none lg:w-20 ${
            selectedFilter === RequestFilterTypeEnum.GRAPHIC
              ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
              : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
          }`}
          color="gray"
          id={RequestFilterTypeEnum.GRAPHIC}
          size="sm"
          variant="secondary"
        >
          Graphic
        </ToggleButton>
      ) : hasExistingRequests.graphic ? (
        renderFilterableLockButton(
          RequestFilterTypeEnum.GRAPHIC,
          "Graphic",
          "flex-1 lg:flex-none lg:w-28",
        )
      ) : (
        renderLockedButton(
          RequestFilterTypeEnum.GRAPHIC,
          "Graphic",
          "flex-1 lg:flex-none lg:w-28",
        )
      )}
      {permissions.hasVideo ? (
        <ToggleButton
          className={`cursor-pointer flex-1 lg:flex-none lg:w-16 ${
            selectedFilter === RequestFilterTypeEnum.VIDEO
              ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
              : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
          }`}
          color="gray"
          id={RequestFilterTypeEnum.VIDEO}
          size="sm"
          variant="secondary"
        >
          Video
        </ToggleButton>
      ) : hasExistingRequests.video ? (
        renderFilterableLockButton(
          RequestFilterTypeEnum.VIDEO,
          "Video",
          "flex-1 lg:flex-none lg:w-24",
        )
      ) : (
        renderLockedButton(
          RequestFilterTypeEnum.VIDEO,
          "Video",
          "flex-1 lg:flex-none lg:w-24",
        )
      )}
      {permissions.hasWebsite ? (
        <ToggleButton
          className={`cursor-pointer flex-1 lg:flex-none lg:w-32 ${
            selectedFilter === RequestFilterTypeEnum.WEBUI
              ? "bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover hover:text-text-white"
              : "text-utility-gray-700 hover:bg-utility-brand-50 hover:text-utility-brand-600 hover:border-utility-brand-200"
          }`}
          color="gray"
          id={RequestFilterTypeEnum.WEBUI}
          size="sm"
          variant="secondary"
        >
          Website & UI
        </ToggleButton>
      ) : hasExistingRequests.website ? (
        renderFilterableLockButton(
          RequestFilterTypeEnum.WEBUI,
          "Website & UI",
          "flex-1 lg:flex-none lg:w-36",
        )
      ) : (
        renderLockedButton(
          RequestFilterTypeEnum.WEBUI,
          "Website & UI",
          "flex-1 lg:flex-none lg:w-36",
        )
      )}
    </ToggleButtonGroup>
  );
}
